import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Select,
  Table,
  Tag,
  Tabs,
  Input,
  Space,
  Typography,
  Empty,
  Drawer,
  Button,
  Form,
  InputNumber,
  Row,
  Col,
  Divider,
  Statistic,
  Progress,
  Tooltip,
  Avatar,
  Modal,
  Upload,
  message,
  Popconfirm,
  Skeleton,
  Alert,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  TeamOutlined,
  WarningOutlined,
  CheckCircleTwoTone,
  ClockCircleTwoTone,
  FileSearchOutlined,
  UserOutlined,
  UploadOutlined,
  DownloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http, type Page } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { ANCHOR_COLORS } from '../utils/scoreColor';

const { Text, Title } = Typography;

interface Person {
  id: number;
  name: string;
  empNo?: string;
  deptPath?: string;
  position?: string;
  status?: string;
}

interface Dimension {
  code: string;
  name: string;
  weight: number;
  anchors: Record<string, string>;
}

interface RedLine {
  code: string;
  desc: string;
}

interface CompetencyModel {
  id: number;
  position: string;
  versionNo: number;
  effectiveAt: string;
  dimensions: Dimension[];
  redLines: RedLine[];
  applicableEmpIds: number[];
  applicablePersons: Person[];
}

interface EmployeeOption {
  id: number;
  name: string;
  empNo?: string;
  deptPath?: string;
  position?: string;
  status?: string;
}

/** 界面展示用中文红线称谓（存储仍为 V1/V2/V3） */
const REDLINE_CN: Record<string, string> = {
  V1: '红线一',
  V2: '红线二',
  V3: '红线三',
};
function redLineCn(code: string) {
  return REDLINE_CN[code] || code;
}

const POSITION_GRADIENTS = [
  'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
  'linear-gradient(135deg,#43cea2 0%,#185a9d 100%)',
  'linear-gradient(135deg,#ff758c 0%,#ff7eb3 100%)',
  'linear-gradient(135deg,#f7971e 0%,#ffd200 100%)',
  'linear-gradient(135deg,#56ccf2 0%,#2f80ed 100%)',
  'linear-gradient(135deg,#a18cd1 0%,#fbc2eb 100%)',
  'linear-gradient(135deg,#11998e 0%,#38ef7d 100%)',
  'linear-gradient(135deg,#ee0979 0%,#ff6a00 100%)',
];

function gradientFor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return POSITION_GRADIENTS[h % POSITION_GRADIENTS.length];
}

/**
 * 模型库列表顺序（与业务梳理一致；职位名字符串须与库内 position / CSV 完全一致）。
 * 同职位多卡片（不同适用人）序号相同，再按 id 稳定排序。
 */
const COMPETENCY_MODEL_POSITION_ORDER: string[] = [
  '前厅接待',
  '分诊结算专员',
  '会员服务经理',
  '美学规划师',
  '美学规划师兼店长',
  '储备总经理助理',
  '助理医&执业医',
  '院长',
  '药房兼行政',
  '护士',
  '护士长&副护士长',
  '区域医务主任',
  '人事专员',
  '薪酬服务bp',
  '行政采购',
  '人事经理',
  '财务专员',
  '资金管理专员',
  '财务经理',
  '战投经理',
  '网络咨询专员',
  '邀约专员',
  '平面设计师',
  '设计总监',
  '摄影剪辑师',
  '摄影剪辑',
  '小红书运营专员',
  '引流经理',
  '产品营销专员',
  '电商运营',
  '私域运营',
  '瑞小美学苑营销负责人',
  '营销总监',
  '销售总监',
  'AI产品开发工程师&AI维护程序员',
  'AI产品经理/售前/售后',
  'AI培训讲师',
  'AI首席运营官COO',
];

const COMPETENCY_MODEL_ORDER_MAP = new Map(
  COMPETENCY_MODEL_POSITION_ORDER.map((p, i) => [p, i]),
);
const UNKNOWN_POSITION_RANK = 1_000_000;

function compareCompetencyModels(a: CompetencyModel, b: CompetencyModel): number {
  const ia = COMPETENCY_MODEL_ORDER_MAP.has(a.position)
    ? COMPETENCY_MODEL_ORDER_MAP.get(a.position)!
    : UNKNOWN_POSITION_RANK;
  const ib = COMPETENCY_MODEL_ORDER_MAP.has(b.position)
    ? COMPETENCY_MODEL_ORDER_MAP.get(b.position)!
    : UNKNOWN_POSITION_RANK;
  if (ia !== ib) return ia - ib;
  const byName = a.position.localeCompare(b.position, 'zh-CN');
  if (byName !== 0) return byName;
  return a.id - b.id;
}

// ========================================================================
// 评价进度（全员 × 进度）—— 与「素质项 · 评价进度」同形态：
//   汇总卡 + 筛选 + 进度条 + 表格（每人一行 + 查看详情）
// ========================================================================
const REDLINE_CN_RT: Record<string, string> = {
  V1: '红线一',
  V2: '红线二',
  V3: '红线三',
};

function shortDeptResults(path: string | null | undefined) {
  if (!path) return '—';
  const parts = path.split(/[\/\\>›→]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(' / ');
  return parts.slice(-2).join(' / ');
}

/** 与后端 CompetencyService.resolveModel 同优先级（前端镜像） */
function resolveCompetencyModelRT(
  models: CompetencyModel[],
  position: string | null | undefined,
  evaluateeId: number,
): CompetencyModel | null {
  if (!models || models.length === 0) return null;
  const empPos = (position || '').trim();

  const explicit = models.find((m) => m.applicableEmpIds?.includes(evaluateeId));
  if (explicit) return explicit;
  if (!empPos) return null;

  const splitPos = (s: string) =>
    s.split(/[&/、，,；;]/).map((x) => x.trim()).filter(Boolean);

  const exact = models.find((m) => (m.position || '').trim() === empPos);
  if (exact) return exact;

  const merged = models.find((m) => splitPos(m.position || '').includes(empPos));
  if (merged) return merged;

  const empParts = splitPos(empPos);
  if (empParts.length > 1) {
    const reverse = models.find((m) => empParts.includes((m.position || '').trim()));
    if (reverse) return reverse;
  }
  return null;
}

interface ResultRow {
  evaluateeId: number;
  evaluatorId: number | null;
  evaluator: { id: number; name: string; empNo?: string } | null;
  evaluatee: {
    id: number;
    name: string;
    empNo?: string;
    deptPath?: string | null;
    position?: string | null;
  };
  weightedScore: number | null;
  redLineFlag: boolean;
  status: string;
  submitAt: string | null;
  assessmentId: number | null;
  dimScores: Array<{ code: string; weight: number; score: number }> | null;
  redLines: Array<{ code: string; hit: boolean; note?: string }> | null;
}

function ResultsTab() {
  const { batches, batchId, setBatchId } = useBatches();
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'pending'>('all');
  const [activeRow, setActiveRow] = useState<ResultRow | null>(null);

  const { data = [], isFetching } = useQuery<ResultRow[]>({
    queryKey: ['competency-admin', batchId],
    queryFn: () => api.get('/admin/competency/results', { params: { batchId } }),
    enabled: !!batchId,
  });

  /** 全量模型（含维度/锚定/红线/适用人员）—— 详情 Modal 用客户端 resolveModel 配对当前下属。 */
  const { data: models = [] } = useQuery<CompetencyModel[]>({
    queryKey: ['admin', 'competency-models'],
    queryFn: () => api.get('/admin/competency/models') as Promise<CompetencyModel[]>,
  });

  const { data: rosterGap } = useQuery<{
    rosterActiveCount: number;
    inSummaryCount: number;
    excludedCount: number;
    excluded: Array<{
      id: number;
      name: string;
      empNo: string;
      position?: string;
      reason: string;
      directLeaderName: string | null;
    }>;
  }>({
    queryKey: ['competency-roster-gap'],
    queryFn: () => api.get('/admin/competency/roster-gap'),
  });

  const stats = useMemo(() => {
    const total = data.length;
    const submitted = data.filter((r) => r.status === 'SUBMITTED').length;
    const pending = total - submitted;
    const redLineCount = data.filter((r) => r.redLineFlag).length;
    const submittedScores = data
      .filter((r) => r.status === 'SUBMITTED' && typeof r.weightedScore === 'number')
      .map((r) => r.weightedScore as number);
    const avg =
      submittedScores.length === 0
        ? 0
        : submittedScores.reduce((s, v) => s + v, 0) / submittedScores.length;
    const percent = total ? Math.round((submitted / total) * 100) : 0;
    return { total, submitted, pending, redLineCount, avg, percent };
  }, [data]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return data.filter((r) => {
      if (statusFilter === 'submitted' && r.status !== 'SUBMITTED') return false;
      if (statusFilter === 'pending' && r.status === 'SUBMITTED') return false;
      if (!kw) return true;
      const hay = [
        r.evaluatee?.name,
        r.evaluatee?.position ?? '',
        r.evaluatee?.deptPath ?? '',
        r.evaluator?.name ?? '',
      ]
        .join(' ');
      return hay.includes(kw);
    });
  }, [data, keyword, statusFilter]);

  const activeModel = useMemo(
    () =>
      activeRow
        ? resolveCompetencyModelRT(models, activeRow.evaluatee.position, activeRow.evaluatee.id)
        : null,
    [models, activeRow],
  );

  /** 维度评分快查 map（code → score / weight） */
  const dimScoreMap = useMemo(() => {
    const m = new Map<string, { score: number; weight: number }>();
    if (activeRow?.dimScores) {
      for (const d of activeRow.dimScores) {
        m.set(d.code, { score: d.score, weight: d.weight });
      }
    }
    return m;
  }, [activeRow]);

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="本页已并入「胜任力 + 高潜 评价汇总」合并页"
        description={
          <span>
            推荐使用合并页查看：胜任力评分 / 红线 / 高潜识别 / 四维 / STAR 一行展示。
            旧页面仍保留过渡用。 <a href="/competency-hp/results">→ 前往合并页</a>
          </span>
        }
      />
      {/* 顶部汇总 + 筛选（与岗位胜任力派单 / 素质项进度 同形态） */}
      <Card style={{ marginBottom: 12 }} bodyStyle={{ paddingBottom: 12 }}>
        <Space wrap size={12}>
          <span>批次：</span>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            value={batchId}
            options={batches.map((b: any) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
          />
          <span style={{ marginLeft: 8 }}>状态：</span>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'submitted', label: '已提交' },
              { value: 'pending', label: '待提交' },
            ]}
          />
          <Input.Search
            placeholder="搜索被评价人 / 直属领导 / 部门 / 职位"
            allowClear
            style={{ width: 280 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </Space>

        <Row gutter={24} style={{ marginTop: 12 }}>
          <Col span={5}>
            <Statistic
              title="应评价"
              value={stats.total}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="已提交"
              value={stats.submitted}
              suffix={stats.total ? `/ ${stats.total}` : ''}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="待提交"
              value={stats.pending}
              valueStyle={{ color: stats.pending ? '#fa8c16' : '#999' }}
              prefix={stats.pending ? <ClockCircleTwoTone twoToneColor="#fa8c16" /> : null}
              suffix="人"
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="平均加权分"
              value={stats.avg}
              precision={2}
              valueStyle={{ color: '#1677ff' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="触发红线"
              value={stats.redLineCount}
              suffix="人"
              valueStyle={{ color: stats.redLineCount ? '#ff4d4f' : '#999' }}
            />
          </Col>
        </Row>
        <Progress
          percent={stats.percent}
          size="small"
          showInfo={false}
          style={{ marginTop: 8 }}
          strokeColor={{ '0%': '#722ed1', '100%': '#52c41a' }}
        />
      </Card>

      {rosterGap && rosterGap.excludedCount > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`花名册在职 ${rosterGap.rosterActiveCount} 人，本表展示 ${rosterGap.inSummaryCount} 人；另有 ${rosterGap.excludedCount} 人未纳入（与汇总规则一致）`}
          description={
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, marginBottom: 0 }}>
              {rosterGap.excluded.map((r) => (
                <li key={r.id}>
                  <strong>{r.name}</strong>
                  {r.position ? ` · ${r.position}` : ''} — {r.reason}
                  {r.directLeaderName ? `（直属：${r.directLeaderName}）` : ''}
                </li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Card bodyStyle={{ paddingTop: 12, paddingBottom: 12 }}>
        <Table<ResultRow>
          rowKey={(r) => `${r.evaluateeId}`}
          loading={isFetching}
          dataSource={filtered}
          size="middle"
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 名被评价人` }}
          columns={[
            {
              title: '被评价人',
              key: 'name',
              width: 160,
              render: (_v, r) => (
                <Space size={8}>
                  <Avatar size={30} icon={<UserOutlined />} style={{ background: '#722ed1' }}>
                    {r.evaluatee?.name?.[0]}
                  </Avatar>
                  <span style={{ fontWeight: 600 }}>{r.evaluatee?.name || '—'}</span>
                </Space>
              ),
            },
            {
              title: '岗位 / 部门',
              key: 'pos',
              ellipsis: true,
              render: (_v, r) => (
                <Space direction="vertical" size={0}>
                  <span style={{ fontSize: 13 }}>{r.evaluatee?.position || '—'}</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {shortDeptResults(r.evaluatee?.deptPath)}
                  </Text>
                </Space>
              ),
            },
            {
              title: '直属领导',
              key: 'leader',
              width: 130,
              render: (_v, r) => r.evaluator?.name || '—',
            },
            {
              title: '加权分',
              dataIndex: 'weightedScore',
              align: 'right' as const,
              width: 100,
              sorter: (a, b) => (a.weightedScore ?? -1) - (b.weightedScore ?? -1),
              render: (v: number | null) =>
                v == null ? <Text type="secondary">—</Text> : <b>{v.toFixed(2)}</b>,
            },
            {
              title: '红线',
              dataIndex: 'redLineFlag',
              align: 'center' as const,
              width: 80,
              render: (v) =>
                v ? (
                  <Tag icon={<WarningOutlined />} color="error">
                    触发
                  </Tag>
                ) : (
                  <Tag>—</Tag>
                ),
            },
            {
              title: '状态',
              dataIndex: 'status',
              align: 'center' as const,
              width: 110,
              render: (v) =>
                v === 'SUBMITTED' ? (
                  <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
                    已提交
                  </Tag>
                ) : (
                  <Tag icon={<ClockCircleTwoTone twoToneColor="#fa8c16" />} color="warning">
                    待提交
                  </Tag>
                ),
            },
            {
              title: '提交时间',
              dataIndex: 'submitAt',
              width: 160,
              render: (v?: string) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '—'),
            },
            {
              title: '操作',
              key: 'action',
              width: 110,
              render: (_v, r) => (
                <Button
                  type="link"
                  size="small"
                  icon={<FileSearchOutlined />}
                  onClick={() => setActiveRow(r)}
                >
                  查看详情
                </Button>
              ),
            },
          ]}
        />
      </Card>

      {/* 详情 Modal：被评价人 / 直属领导 / 维度评分明细（每维 5 档锚定 + 选中分） / 红线判定 / 提交快照 */}
      <Modal
        open={!!activeRow}
        onCancel={() => setActiveRow(null)}
        footer={null}
        width={860}
        destroyOnClose
        title={
          activeRow ? (
            <Space size={8}>
              <Avatar size={30} icon={<UserOutlined />} style={{ background: '#722ed1' }}>
                {activeRow.evaluatee?.name?.[0]}
              </Avatar>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{activeRow.evaluatee?.name}</span>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {activeRow.evaluatee?.position || '—'} · {shortDeptResults(activeRow.evaluatee?.deptPath)}
              </Text>
              {activeRow.status === 'SUBMITTED' ? (
                <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
                  已提交
                </Tag>
              ) : (
                <Tag icon={<ClockCircleTwoTone twoToneColor="#fa8c16" />} color="warning">
                  待提交
                </Tag>
              )}
            </Space>
          ) : (
            '评价详情'
          )
        }
      >
        {activeRow && (
          <div>
            {/* 概要卡 */}
            <Card size="small" style={{ marginBottom: 12, background: '#f5f7ff' }}>
              <Space size="middle" wrap>
                <span>
                  直属领导（评价人）：<b>{activeRow.evaluator?.name || '—'}</b>
                </span>
                {activeRow.weightedScore != null && (
                  <span>
                    加权分：<b style={{ color: '#1677ff' }}>{activeRow.weightedScore.toFixed(2)}</b>
                  </span>
                )}
                {activeRow.redLineFlag && (
                  <Tag color="error" icon={<WarningOutlined />}>
                    存在红线命中
                  </Tag>
                )}
                {activeRow.submitAt && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    提交时间 {new Date(activeRow.submitAt).toLocaleString('zh-CN', { hour12: false })}
                  </Text>
                )}
                {activeModel && <Tag color="purple">v{activeModel.versionNo}</Tag>}
              </Space>
            </Card>

            {/* 待提交 + 没有 model 时给出提示 */}
            {activeRow.status !== 'SUBMITTED' && !activeModel && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="该下属尚未提交评价，且未在模型库匹配到其岗位的胜任力模型"
                description={
                  <span>
                    请到「岗位胜任力评价 · 模型库」检查模型职位名是否覆盖
                    <Tag style={{ margin: '0 4px' }}>{activeRow.evaluatee?.position || '—'}</Tag>
                    或在该岗位模型的「适用人员」里勾选
                    <Tag style={{ margin: '0 4px' }}>{activeRow.evaluatee?.name}</Tag>
                  </span>
                }
              />
            )}

            {/* 模型存在：展示维度 + 评分（如已提交）。未提交时仅展示锚定，提示「等待评价人提交」 */}
            {activeModel && (
              <>
                <Title level={5} style={{ margin: '4px 0 12px' }}>
                  <span>评价维度</span>
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
                    （每维 5 档锚定 · 1 分最差，5 分最优）
                  </Text>
                </Title>

                {activeRow.status !== 'SUBMITTED' && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="尚未提交，下方仅展示模型锚定"
                    description="等待该下属的直属领导在「岗位胜任力评价」中提交后，这里会显示具体每维选中的分数与红线判定。"
                  />
                )}

                {activeModel.dimensions.map((d, i) => {
                  const got = dimScoreMap.get(d.code);
                  return (
                    <Card
                      key={d.code || i}
                      size="small"
                      style={{ marginBottom: 10 }}
                      title={
                        <Space>
                          <Tag color="blue">维度 {i + 1}</Tag>
                          <span>{d.name}</span>
                          <Tag color="purple">权重 {d.weight}%</Tag>
                          {got && (
                            <Tag color={ANCHOR_COLORS[String(got.score)]}>
                              评价人选 · {got.score} 分
                            </Tag>
                          )}
                        </Space>
                      }
                    >
                      {[1, 2, 3, 4, 5].map((n) => {
                        const isSelected = !!got && got.score === n;
                        return (
                          <div
                            key={n}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 10,
                              padding: '5px 0',
                              borderBottom: n === 5 ? 'none' : '1px dashed #f0f0f0',
                              background: isSelected ? '#fffbe6' : 'transparent',
                              borderRadius: 4,
                            }}
                          >
                            <Tag
                              color={ANCHOR_COLORS[String(n)]}
                              style={{ minWidth: 44, textAlign: 'center', margin: 0 }}
                            >
                              {n} 分
                            </Tag>
                            <span style={{ color: '#444', flex: 1, lineHeight: 1.7 }}>
                              {d.anchors?.[String(n)] || '—'}
                              {isSelected && (
                                <Tag color="gold" style={{ marginLeft: 8 }}>
                                  ← 已选
                                </Tag>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </Card>
                  );
                })}

                {/* 红线判定 */}
                <Divider style={{ margin: '16px 0 12px' }} />
                <Title level={5} style={{ margin: 0 }}>
                  <WarningOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
                  红线（任一触发即取消晋升评优资格）
                </Title>
                {activeModel.redLines.length === 0 ? (
                  <Empty description="未配置红线" style={{ margin: '12px 0' }} />
                ) : (
                  <div style={{ marginTop: 10 }}>
                    {activeModel.redLines.map((r) => {
                      const submitted = activeRow.redLines?.find((x) => x.code === r.code);
                      const hit = !!submitted?.hit;
                      const note = submitted?.note;
                      return (
                        <Card
                          key={r.code}
                          size="small"
                          style={{
                            marginBottom: 8,
                            borderLeft: hit ? '4px solid #ff4d4f' : '4px solid #d9d9d9',
                          }}
                          bodyStyle={{ padding: 12 }}
                        >
                          <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Space align="start">
                              <Tag color={hit ? 'red' : 'default'}>{REDLINE_CN_RT[r.code] || r.code}</Tag>
                              <span style={{ color: '#444' }}>{r.desc}</span>
                            </Space>
                            {activeRow.status === 'SUBMITTED' && (
                              <Tag color={hit ? 'error' : 'success'}>
                                {hit ? '已触发' : '未触发'}
                              </Tag>
                            )}
                          </Space>
                          {hit && note && (
                            <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>
                              备注：{note}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ========================================================================
// 模型库（卡片 + 抽屉详情/编辑）
// ========================================================================
function ModelCard({
  model,
  onView,
  onEdit,
}: {
  model: CompetencyModel;
  onView: () => void;
  onEdit: () => void;
}) {
  const totalWeight = model.dimensions.reduce((s, d) => s + (Number(d.weight) || 0), 0);
  const weightOk = Math.abs(totalWeight - 100) < 0.01;
  const gradient = gradientFor(model.position);

  return (
    <Card
      hoverable
      bodyStyle={{ padding: 0 }}
      style={{
        overflow: 'hidden',
        height: '100%',
        borderRadius: 10,
        border: '1px solid rgba(15,23,42,0.06)',
        boxShadow: '0 6px 16px rgba(15,23,42,0.06)',
      }}
    >
      <div
        style={{
          background: gradient,
          padding: '12px 14px 10px',
          color: '#fff',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}>{model.position}</div>
          </div>
          {model.applicablePersons.length > 0 ? (
            <Avatar.Group max={{ count: 2 }} size={28}>
              {model.applicablePersons.slice(0, 4).map((p) => (
                <Tooltip key={p.id} title={p.name}>
                  <Avatar style={{ background: 'rgba(255,255,255,0.3)', color: '#fff' }}>
                    {p.name?.slice(-1) || '?'}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
          ) : null}
        </div>
      </div>

      <div style={{ padding: 12 }}>
        <Row gutter={6} style={{ marginBottom: 8 }}>
          <Col span={model.applicablePersons.length > 0 ? 8 : 12}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1677ff', lineHeight: 1.2 }}>
                {model.dimensions.length}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                维度
              </Text>
            </div>
          </Col>
          <Col span={model.applicablePersons.length > 0 ? 8 : 12}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: model.redLines.length === 3 ? '#52c41a' : '#fa8c16',
                }}
              >
                {model.redLines.length}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                红线
              </Text>
            </div>
          </Col>
          {model.applicablePersons.length > 0 ? (
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#722ed1', lineHeight: 1.2 }}>
                  {model.applicablePersons.length}
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  指定人员
                </Text>
              </div>
            </Col>
          ) : null}
        </Row>
        {model.applicablePersons.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8, lineHeight: 1.4 }}>
            未绑定具体人员：领导评价时按<strong>职位</strong>自动匹配本模型（与数据库「优先人员命中、否则职位默认」规则一致）。
          </Text>
        ) : null}

        <Tooltip title={`权重合计 ${totalWeight}%（应为 100%）`}>
          <Progress
            percent={Math.min(100, totalWeight)}
            size="small"
            status={weightOk ? 'success' : 'exception'}
            style={{ marginBottom: 8 }}
          />
        </Tooltip>

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button size="small" icon={<EyeOutlined />} onClick={onView}>
            查看详情
          </Button>
          <Button size="small" type="primary" icon={<EditOutlined />} onClick={onEdit}>
            编辑
          </Button>
        </Space>
      </div>
    </Card>
  );
}

function ModelDetail({ model }: { model: CompetencyModel }) {
  const totalWeight = model.dimensions.reduce((s, d) => s + (Number(d.weight) || 0), 0);
  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="维度" value={model.dimensions.length} suffix="项" />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="权重合计"
              value={totalWeight}
              suffix="%"
              valueStyle={{ color: Math.abs(totalWeight - 100) < 0.01 ? '#52c41a' : '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="适用人员"
              value={model.applicablePersons.length}
              suffix="人"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Title level={5} style={{ marginBottom: 12 }}>
        评价维度（{model.dimensions.length} 项维度 · 每维 5 档锚定）
      </Title>
      {model.dimensions.map((d, i) => (
        <Card
          key={d.code || i}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <Tag color="blue">维度 {i + 1}</Tag>
              <span>{d.name}</span>
              <Tag color="purple">权重 {d.weight}%</Tag>
            </Space>
          }
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '6px 0',
                borderBottom: n === 5 ? 'none' : '1px dashed #f0f0f0',
              }}
            >
              <Tag color={ANCHOR_COLORS[String(n)]} style={{ minWidth: 44, textAlign: 'center', margin: 0 }}>
                {n} 分
              </Tag>
              <span style={{ color: '#444', flex: 1 }}>{d.anchors?.[String(n)] || '—'}</span>
            </div>
          ))}
        </Card>
      ))}

      <Divider />
      <Title level={5}>
        <WarningOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
        红线（触任一项 → 取消晋升评优资格）
      </Title>
      {model.redLines.length === 0 ? (
        <Empty description="未配置红线" />
      ) : (
        model.redLines.map((r) => (
          <Card
            key={r.code}
            size="small"
            style={{ marginBottom: 8, borderLeft: '4px solid #ff4d4f' }}
            bodyStyle={{ padding: 12 }}
          >
            <Space align="start">
              <Tag color="red">{redLineCn(r.code)}</Tag>
              <span style={{ color: '#444' }}>{r.desc}</span>
            </Space>
          </Card>
        ))
      )}

      <Divider />
      <Title level={5}>
        <TeamOutlined style={{ marginRight: 6 }} />
        {model.applicablePersons.length > 0
          ? `指定适用人员（${model.applicablePersons.length}）`
          : '适用人员'}
      </Title>
      {model.applicablePersons.length === 0 ? (
        <Text type="secondary">
          未单独绑定人员：该职位下员工在领导端评价时，系统自动按职位选用本模型。
        </Text>
      ) : (
        <Space wrap size={[8, 8]}>
          {model.applicablePersons.map((p) => (
            <Tag
              key={p.id}
              color={p.status === 'ACTIVE' ? 'geekblue' : 'default'}
              style={{ padding: '4px 10px', fontSize: 13 }}
            >
              {p.name}
              {p.deptPath ? ` · ${p.deptPath.split('/').pop()}` : ''}
            </Tag>
          ))}
        </Space>
      )}
    </div>
  );
}

function ModelEditForm({
  model,
  employees,
  onCancel,
  onSaved,
}: {
  model: CompetencyModel;
  employees: EmployeeOption[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      dimensions: model.dimensions.map((d) => ({
        code: d.code,
        name: d.name,
        weight: d.weight,
        anchor1: d.anchors?.['1'] || '',
        anchor2: d.anchors?.['2'] || '',
        anchor3: d.anchors?.['3'] || '',
        anchor4: d.anchors?.['4'] || '',
        anchor5: d.anchors?.['5'] || '',
      })),
      redLines: ['V1', 'V2', 'V3'].map((code) => {
        const found = model.redLines.find((r) => r.code === code);
        return { code, desc: found?.desc || '' };
      }),
      applicableEmpIds: model.applicableEmpIds || [],
    });
  }, [model, form]);

  const dimensions = Form.useWatch('dimensions', form) || [];
  const totalWeight = (dimensions as any[]).reduce(
    (s, d) => s + (Number(d?.weight) || 0),
    0,
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        dimensions: (values.dimensions as any[]).map((d, idx) => ({
          code: d.code || `D${idx + 1}`,
          name: d.name,
          weight: Number(d.weight),
          anchors: {
            '1': d.anchor1,
            '2': d.anchor2,
            '3': d.anchor3,
            '4': d.anchor4,
            '5': d.anchor5,
          },
        })),
        redLines: (values.redLines as any[]).map((r) => ({ code: r.code, desc: r.desc })),
        applicableEmpIds: values.applicableEmpIds || [],
      };
      setSubmitting(true);
      await api.put(`/admin/competency/models/${model.id}`, payload);
      message.success('已保存');
      onSaved();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form form={form} layout="vertical" disabled={submitting}>
      <Card
        size="small"
        style={{ marginBottom: 16, background: '#f5f7ff' }}
        bodyStyle={{ padding: 12 }}
      >
        <Space size="middle">
          <Text>
            当前编辑：<b>{model.position}</b>
          </Text>
          <Text type={Math.abs(totalWeight - 100) < 0.01 ? 'success' : 'danger'}>
            权重合计 {totalWeight}%（应为 100%）
          </Text>
        </Space>
      </Card>

      <Title level={5}>评价维度</Title>
      <Form.List name="dimensions">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, i) => (
              <Card
                key={field.key}
                size="small"
                style={{ marginBottom: 12 }}
                title={
                  <Space>
                    <Tag color="blue">维度 {i + 1}</Tag>
                    <Form.Item
                      {...field}
                      noStyle
                      name={[field.name, 'name']}
                      rules={[{ required: true, message: '请填写维度名称' }]}
                    >
                      <Input placeholder="维度名称" style={{ width: 320 }} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      noStyle
                      name={[field.name, 'weight']}
                      rules={[{ required: true, message: '请填写权重' }]}
                    >
                      <InputNumber min={1} max={100} addonAfter="%" placeholder="权重" />
                    </Form.Item>
                  </Space>
                }
                extra={
                  fields.length > 1 ? (
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                    >
                      删除
                    </Button>
                  ) : null
                }
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <Form.Item
                    {...field}
                    key={n}
                    label={
                      <Tag color={ANCHOR_COLORS[String(n)]} style={{ margin: 0 }}>
                        {n} 分
                      </Tag>
                    }
                    name={[field.name, `anchor${n}`]}
                    rules={[{ required: true, message: `请填写 ${n} 分锚定描述` }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                  </Form.Item>
                ))}
              </Card>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() =>
                add({
                  code: `D${fields.length + 1}`,
                  name: '',
                  weight: 0,
                  anchor1: '',
                  anchor2: '',
                  anchor3: '',
                  anchor4: '',
                  anchor5: '',
                })
              }
            >
              新增维度
            </Button>
          </>
        )}
      </Form.List>

      <Divider />

      <Title level={5}>
        <WarningOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
        红线（三项）
      </Title>
      <Form.List name="redLines">
        {(fields) => (
          <>
            {fields.map((field, i) => (
              <Form.Item
                {...field}
                key={field.key}
                label={
                  <Space>
                    <Tag color="red">{redLineCn(['V1', 'V2', 'V3'][i] || `V${i + 1}`)}</Tag>
                    <span>说明</span>
                  </Space>
                }
                name={[field.name, 'desc']}
                rules={[
                  {
                    required: true,
                    message: `请填写${redLineCn(['V1', 'V2', 'V3'][i] || '')}的说明`,
                  },
                ]}
              >
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
              </Form.Item>
            ))}
          </>
        )}
      </Form.List>

      <Divider />

      <Title level={5}>
        <TeamOutlined style={{ marginRight: 6 }} />
        适用人员
      </Title>
      <Form.Item
        name="applicableEmpIds"
        rules={[{ required: false }]}
        extra="可多选；同一职位但适用人员不同时，应该是不同的模型"
      >
        <Select
          mode="multiple"
          placeholder="搜索员工姓名 / 工号 / 部门"
          showSearch
          filterOption={(input, option: any) => {
            const kw = input.toLowerCase();
            const label = (option?.label ?? '').toLowerCase();
            const search = (option?.search ?? '').toLowerCase();
            return label.includes(kw) || search.includes(kw);
          }}
          options={employees.map((e) => ({
            value: e.id,
            label: `${e.name}${e.deptPath ? ` · ${e.deptPath.split('/').pop()}` : ''}${e.position ? ` · ${e.position}` : ''}`,
            search: `${e.name} ${e.empNo ?? ''} ${e.deptPath ?? ''} ${e.position ?? ''}`,
          }))}
          maxTagCount="responsive"
        />
      </Form.Item>

      <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: 12, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            保存
          </Button>
        </Space>
      </div>
    </Form>
  );
}

function ModelsTab() {
  const [keyword, setKeyword] = useState('');
  const [drawerModel, setDrawerModel] = useState<CompetencyModel | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const queryClient = useQueryClient();

  /** Slice #7：批量导入岗位胜任力模型 */
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<UploadFile | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<{
    totalPositions: number;
    created: number;
    errors: Array<{ position: string; row?: number; message: string }>;
    pending: Array<{
      position: string;
      modelId: number;
      token: string;
      candidates: Array<{
        id: number;
        empNo: string;
        name: string;
        position: string | null;
        deptPath: string | null;
        wasPreviouslyBound: boolean;
      }>;
    }>;
    warnings: Array<{ position: string; message: string }>;
    imported: Array<{ position: string; modelId: number }>;
  } | null>(null);
  const [pendingSelections, setPendingSelections] = useState<
    Record<string, { modelId: number; empId: number | null }>
  >({});
  const [pendingBusyKey, setPendingBusyKey] = useState<string | null>(null);
  const [importHadPending, setImportHadPending] = useState(false);

  const { data = [], isFetching } = useQuery<CompetencyModel[]>({
    queryKey: ['competency-models'],
    queryFn: () => api.get('/admin/competency/models'),
  });

  const downloadImportTemplate = async () => {
    try {
      const r = await http.get('/admin/competency/models/import-template.xlsx', {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '岗位胜任力导入模版.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(`模版下载失败：${(e as Error).message}`);
    }
  };

  const exportAllModels = async () => {
    try {
      const r = await http.get('/admin/competency/models/export.xlsx', {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `岗位胜任力模型-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('已导出当前全部岗位胜任力模型（每职位最新版）');
    } catch (e) {
      message.error(`导出失败：${(e as Error).message}`);
    }
  };

  const closeImportModal = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
    setPendingSelections({});
    setImportHadPending(false);
    setPendingBusyKey(null);
  };

  /** 导入已跑完且同名待处理已清空（或本就没有）→ 只显示「确定」关弹窗 */
  const importFinished =
    !!importResult && importResult.pending.length === 0;

  const doImport = async () => {
    if (!importFile?.originFileObj) {
      message.warning('请先选择 .xls 或 .xlsx 文件');
      return;
    }
    setImportBusy(true);
    setPendingSelections({});
    setImportHadPending(false);
    try {
      const fd = new FormData();
      fd.append('file', importFile.originFileObj as Blob);
      const result = (await api.post(
        '/admin/competency/models/import-excel',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )) as unknown as NonNullable<typeof importResult>;
      setImportResult(result);
      setImportHadPending(result.pending.length > 0);
      const pendingInit: Record<string, { modelId: number; empId: number | null }> = {};
      for (const [i, p] of result.pending.entries()) {
        const key = `pending-${i}`;
        const prevBound = p.candidates.filter((c) => c.wasPreviouslyBound);
        pendingInit[key] = {
          modelId: p.modelId,
          empId: prevBound.length === 1 ? prevBound[0].id : null,
        };
      }
      setPendingSelections(pendingInit);
      const pendingN = result.pending.length;
      message.success(
        `导入完成：${result.created}/${result.totalPositions} 个职位${
          pendingN ? `；同名待处理 ${pendingN} 人` : ''
        }${result.errors.length ? `；错误 ${result.errors.length}` : ''}`,
      );
      queryClient.invalidateQueries({ queryKey: ['competency-models'] });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(`导入失败：${err?.response?.data?.message ?? err?.message ?? '未知错误'}`);
    } finally {
      setImportBusy(false);
    }
  };

  const confirmPendingAssign = async (
    key: string,
    item: NonNullable<typeof importResult>['pending'][number],
  ) => {
    const sel = pendingSelections[key];
    if (!sel?.empId) {
      message.warning('请先选择员工');
      return;
    }
    setPendingBusyKey(key);
    try {
      await api.post(`/admin/competency/models/${sel.modelId}/applicable-emps`, {
        empIds: [sel.empId],
      });
      message.success(`已将 ${item.token} 划入岗位`);
      const idx = Number(key.replace('pending-', ''));
      setImportResult((prev) => {
        if (!prev) return prev;
        const nextPending = prev.pending.filter((_, i) => i !== idx);
        const nextSel: Record<string, { modelId: number; empId: number | null }> = {};
        for (const [i, p] of nextPending.entries()) {
          const oldKey = `pending-${i >= idx ? i + 1 : i}`;
          const oldSel = pendingSelections[oldKey];
          const prevBound = p.candidates.filter((c) => c.wasPreviouslyBound);
          nextSel[`pending-${i}`] = oldSel ?? {
            modelId: p.modelId,
            empId: prevBound.length === 1 ? prevBound[0].id : null,
          };
        }
        setPendingSelections(nextSel);
        return { ...prev, pending: nextPending };
      });
      queryClient.invalidateQueries({ queryKey: ['competency-models'] });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(`划入失败：${err?.response?.data?.message ?? err?.message ?? '未知错误'}`);
    } finally {
      setPendingBusyKey(null);
    }
  };

  const { data: employeesPage } = useQuery<Page<EmployeeOption>>({
    queryKey: ['competency-models-employee-options'],
    queryFn: () =>
      api.get('/admin/employees', {
        params: { status: 'ACTIVE', page: 1, size: 3000 },
      }),
    enabled: drawerMode === 'edit' && !!drawerModel,
  });
  const employees: EmployeeOption[] = employeesPage?.items || [];

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    const list = !k
      ? [...data]
      : data.filter((m) => {
          if (m.position.toLowerCase().includes(k)) return true;
          if (m.applicablePersons.some((p) => p.name.toLowerCase().includes(k))) return true;
          return false;
        });
    list.sort(compareCompetencyModels);
    return list;
  }, [data, keyword]);

  /** 写入各模型「指定人员」的在职员工，跨模型去重（避免简单相加导致与花名册对不上） */
  const totalPersonsBoundUnique = useMemo(() => {
    const ids = new Set<number>();
    for (const m of data) {
      for (const p of m.applicablePersons) {
        if (p.status === 'ACTIVE' || p.status == null) ids.add(p.id);
      }
    }
    return ids.size;
  }, [data]);
  const totalDimensions = data.reduce((s, m) => s + m.dimensions.length, 0);
  const totalRedLines = data.reduce((s, m) => s + m.redLines.length, 0);

  return (
    <div style={{ background: 'linear-gradient(180deg, #f4f7fc 0%, #fafbfe 120px)', margin: -12, padding: 12, borderRadius: 8 }}>
      <Card
        style={{
          marginBottom: 20,
          borderRadius: 12,
          border: 'none',
          boxShadow: '0 4px 18px rgba(15,23,42,0.06)',
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} lg={5}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                胜任力模型库
              </Text>
              <Title level={5} style={{ margin: '2px 0 0' }}>
                职位 × 评价内容
              </Title>
              <Text type="secondary" style={{ fontSize: 11 }}>
                卡片可查看或编辑。右侧「指定人员」为写入模板的在职人数（去重），不含仅走职位默认或未建模板的高管等。
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Statistic
              title="职位模型"
              value={data.length}
              suffix="个"
              valueStyle={{ color: '#1677ff', fontWeight: 600 }}
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Statistic
              title="指定人员（去重·在职）"
              value={totalPersonsBoundUnique}
              suffix="人"
              valueStyle={{ color: '#722ed1', fontWeight: 600 }}
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Statistic title="维度合计" value={totalDimensions} suffix="项" valueStyle={{ fontWeight: 600 }} />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Statistic
              title="红线合计"
              value={totalRedLines}
              suffix="条"
              valueStyle={{ color: '#ff4d4f', fontWeight: 600 }}
            />
          </Col>
          <Col xs={24} lg={3}>
            <Input
              placeholder="按职位 / 人员姓名搜索"
              prefix={<SearchOutlined />}
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} lg={4}>
            <Space wrap size={[6, 6]}>
              <Tooltip title="导出当前全部职位的最新版模型为 Excel（列结构与导入模版一致）">
                <Button size="small" icon={<ExportOutlined />} onClick={exportAllModels}>
                  导出
                </Button>
              </Tooltip>
              <Tooltip title="下载导入模版（含表头 + 示例）">
                <Button size="small" icon={<DownloadOutlined />} onClick={downloadImportTemplate}>
                  模版
                </Button>
              </Tooltip>
              <Tooltip title="按职位批量导入：同职位多行视为同一模型，已存在的职位会覆盖更新">
                <Button
                  size="small"
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setImportFile(null);
                    setImportResult(null);
                    setImportOpen(true);
                  }}
                >
                  导入
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {isFetching ? (
        <Row gutter={[12, 12]} wrap>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Col key={i} xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}>
              <Card size="small" style={{ borderRadius: 10 }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      ) : filtered.length === 0 ? (
        <Card>
          <Empty description="没有找到匹配的胜任力模型" />
        </Card>
      ) : (
        <Row gutter={[12, 12]} wrap>
          {filtered.map((m) => (
            <Col key={m.id} xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}>
              <ModelCard
                model={m}
                onView={() => {
                  setDrawerModel(m);
                  setDrawerMode('view');
                }}
                onEdit={() => {
                  setDrawerModel(m);
                  setDrawerMode('edit');
                }}
              />
            </Col>
          ))}
        </Row>
      )}

      <Drawer
        open={!!drawerModel}
        onClose={() => setDrawerModel(null)}
        width={drawerMode === 'edit' ? 880 : 720}
        title={
          drawerModel ? (
            <Space>
              <Title level={4} style={{ margin: 0 }}>
                {drawerModel.position}
              </Title>
              {drawerMode === 'view' && (
                <Button
                  size="small"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setDrawerMode('edit')}
                >
                  编辑
                </Button>
              )}
            </Space>
          ) : null
        }
        destroyOnClose
      >
        {drawerModel &&
          (drawerMode === 'view' ? (
            <ModelDetail model={drawerModel} />
          ) : (
            <ModelEditForm
              model={drawerModel}
              employees={employees}
              onCancel={() => setDrawerMode('view')}
              onSaved={() => {
                queryClient.invalidateQueries({ queryKey: ['competency-models'] });
                setDrawerModel(null);
              }}
            />
          ))}
      </Drawer>

      {/* === Slice #7：岗位胜任力模型批量导入 Modal === */}
      <Modal
        title="批量导入岗位胜任力模型（Excel）"
        open={importOpen}
        onCancel={closeImportModal}
        onOk={importFinished ? closeImportModal : doImport}
        confirmLoading={importBusy}
        okText={importFinished ? '确定' : '开始导入'}
        cancelText="关闭"
        cancelButtonProps={importFinished ? { style: { display: 'none' } } : undefined}
        okButtonProps={
          importResult && importResult.pending.length > 0
            ? { style: { display: 'none' } }
            : undefined
        }
        destroyOnClose
        width={720}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type="info"
            showIcon
            message="单 Sheet，每行 = 一个维度，同职位多行视为同一模型"
            description={
              <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12, color: '#475569' }}>
                <li>列：职位 / 维度顺序 / 维度名称 / 权重(%) / 1~5分锚点 / 红线一~三 / 适用人员（姓名或工号）</li>
                <li>「职位」列只需要在每组首行填，后续维度行留空即可自动复用上一个职位</li>
                <li>红线和适用人员只在该职位首行填一次；适用人员填<strong>姓名</strong>即可（同名多人时改填工号），多个用 <Text code>/</Text>、<Text code>;</Text>、<Text code>,</Text> 任一分隔</li>
                <li>同名多人不再导致职位导入失败，会进入「同名待处理」清单，请按工号手动选择并划入岗位；未手动划入的同名员工仍会按「职位名称匹配」兜底命中评价模型，手动划入主要用于同职位多模型或指定人员场景</li>
                <li>查无此人仅产生警告，不阻断该职位导入（不自动新建员工）</li>
                <li>同职位维度权重之和必须 = 100；已存在的职位会<strong>覆盖更新</strong>当前模型</li>
              </ul>
            }
          />
          <Space wrap>
            <Button icon={<DownloadOutlined />} onClick={downloadImportTemplate}>
              下载空模版
            </Button>
            <Button icon={<ExportOutlined />} onClick={exportAllModels}>
              导出当前模型作为修改起点
            </Button>
          </Space>
          <Upload.Dragger
            maxCount={1}
            beforeUpload={() => false}
            fileList={importFile ? [importFile] : []}
            onChange={(info) => setImportFile(info.fileList[info.fileList.length - 1] ?? null)}
            accept=".xls,.xlsx"
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p>点击或拖拽 .xls / .xlsx 文件到此处</p>
          </Upload.Dragger>
          {importResult && (
            <>
              <Alert
                type={
                  importResult.errors.length
                    ? 'warning'
                    : importResult.pending.length
                      ? 'info'
                      : 'success'
                }
                showIcon
                message={`导入 ${importResult.created} / 共 ${importResult.totalPositions} 个职位${
                  importResult.pending.length
                    ? `；同名待处理 ${importResult.pending.length} 人`
                    : ''
                }${importResult.errors.length ? `；错误 ${importResult.errors.length}` : ''}${
                  importResult.warnings.length ? `；警告 ${importResult.warnings.length}` : ''
                }`}
                description={
                  importResult.errors.length ? (
                    <div style={{ maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
                      {importResult.errors.map((er, i) => (
                        <div key={i}>
                          <strong>{er.position}</strong>
                          {er.row ? `（第 ${er.row} 行）` : ''}：{er.message}
                        </div>
                      ))}
                    </div>
                  ) : undefined
                }
              />
              {importResult.warnings.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={`人员匹配警告（${importResult.warnings.length} 条）`}
                  description={
                    <div style={{ maxHeight: 160, overflow: 'auto', fontSize: 12 }}>
                      {importResult.warnings.map((w, i) => (
                        <div key={i}>
                          <strong>{w.position}</strong>：{w.message}
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
              {importResult.pending.length > 0 && (
                <div
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: 12,
                    background: '#f8fafc',
                  }}
                >
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    关闭弹窗后待处理清单会丢弃；可重新导入同一文件再次生成清单
                  </Typography.Text>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                    同名待处理（{importResult.pending.length}）
                  </Typography.Text>
                  <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    {importResult.pending.map((item, idx) => {
                      const key = `pending-${idx}`;
                      const sel = pendingSelections[key] ?? {
                        modelId: item.modelId,
                        empId: null,
                      };
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 8,
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: idx < importResult.pending.length - 1 ? '1px dashed #e2e8f0' : undefined,
                          }}
                        >
                          <Typography.Text style={{ minWidth: 120 }}>
                            {item.token} · {item.position}
                          </Typography.Text>
                          <Select
                            size="small"
                            style={{ minWidth: 140 }}
                            placeholder="划入岗位"
                            value={sel.modelId}
                            options={importResult.imported.map((m) => ({
                              value: m.modelId,
                              label: m.position,
                            }))}
                            onChange={(modelId) =>
                              setPendingSelections((prev) => ({
                                ...prev,
                                [key]: { ...sel, modelId },
                              }))
                            }
                          />
                          <Select
                            size="small"
                            style={{ minWidth: 280 }}
                            placeholder="选择员工"
                            value={sel.empId ?? undefined}
                            options={item.candidates.map((c) => ({
                              value: c.id,
                              label: (
                                <span>
                                  {c.empNo} · {c.name} · {c.position || '—'} ·{' '}
                                  {shortDeptResults(c.deptPath)}
                                  {c.wasPreviouslyBound && (
                                    <Tag color="blue" style={{ marginLeft: 6 }}>
                                      上次已选
                                    </Tag>
                                  )}
                                </span>
                              ),
                            }))}
                            onChange={(empId) =>
                              setPendingSelections((prev) => ({
                                ...prev,
                                [key]: { ...sel, empId },
                              }))
                            }
                          />
                          <Button
                            size="small"
                            type="primary"
                            loading={pendingBusyKey === key}
                            onClick={() => confirmPendingAssign(key, item)}
                          >
                            确认划入
                          </Button>
                        </div>
                      );
                    })}
                  </Space>
                </div>
              )}
              {importHadPending &&
                importResult.pending.length === 0 &&
                importResult.errors.length === 0 && (
                  <Alert type="success" showIcon message="同名待处理已全部完成" />
                )}
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
}

/**
 * 拆分后的两个独立页面：分别承载侧边栏「模型库」和「评价汇总」两个二级菜单。
 * 旧的 CompetencyAdminPage（带 Tabs 的合并页）保留导出，主要用于路由兼容。
 */
export function CompetencyModelsPage() {
  return (
    <div>
      <div className="tg-page-title">岗位胜任力 · 模型库</div>
      <ModelsTab />
    </div>
  );
}

export function CompetencyResultsPage() {
  return (
    <div>
      <div className="tg-page-title">岗位胜任力 · 评价汇总</div>
      <ResultsTab />
    </div>
  );
}

export function CompetencyAdminPage() {
  return (
    <div>
      <div className="tg-page-title">岗位胜任力</div>
      <Tabs
        defaultActiveKey="models"
        items={[
          { key: 'models', label: '模型库（评价内容）', children: <ModelsTab /> },
          { key: 'results', label: '评价汇总（领导提交结果）', children: <ResultsTab /> },
        ]}
        style={{ marginTop: 8 }}
      />
    </div>
  );
}
